import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Signup from './components/Signup';
import ProtectedRoute from './components/ProtectedRoute';
import Profile from './components/Profile';
import GamesList from './components/games/GamesList';
import GameDetail from './components/games/GameDetail';
import CreateGame from './components/games/CreateGame';
import Notifications from './components/Notifications';
import { supabase } from './lib/supabase';

function App() {
  const { user, userProfile, loading, signOut, refreshProfile } = useAuth();
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [currentPage, setCurrentPage] = useState('games'); // 'profile', 'games', or 'game-detail'
  const [selectedGame, setSelectedGame] = useState(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showCreateGame, setShowCreateGame] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [activeTab, setActiveTab] = useState('explore'); // 'explore', 'watchlist', 'expired'
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [tabsWrapperPaddingTop, setTabsWrapperPaddingTop] = useState(100);
  const [tabsVisible, setTabsVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  
  // Calculate tabs wrapper padding based on header height - 10px from main menu
  useEffect(() => {
    const updateTabsPadding = () => {
      const header = document.querySelector('.user-header');
      if (header) {
        const headerRect = header.getBoundingClientRect();
        setTabsWrapperPaddingTop(headerRect.bottom + 10); // 10px gap from main menu to tabs
      }
    };

    updateTabsPadding();
    window.addEventListener('resize', updateTabsPadding);
    
    const header = document.querySelector('.user-header');
    let resizeObserver;
    if (header && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateTabsPadding);
      resizeObserver.observe(header);
    }

    return () => {
      window.removeEventListener('resize', updateTabsPadding);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [user, loading]);

  // Handle scroll to hide/show tabs
  useEffect(() => {
    // Only handle scroll when tabs are visible (games page without selected game)
    if (currentPage !== 'games' || selectedGame) {
      setTabsVisible(true);
      lastScrollYRef.current = 0;
      return;
    }

    const handleScroll = () => {
      // Get scroll position from multiple sources for compatibility
      const scrollY = Math.max(
        window.scrollY || 0,
        window.pageYOffset || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      );
      
      const scrollThreshold = 25; // Tabs hide when scrolling down past 25px
      const shouldShow = scrollY <= scrollThreshold;
      
      // Always update state to ensure React re-renders
      setTabsVisible(shouldShow);
      lastScrollYRef.current = scrollY;
    };

    // Initial check on mount
    const initialScrollY = Math.max(
      window.scrollY || 0,
      window.pageYOffset || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0
    );
    lastScrollYRef.current = initialScrollY;
    setTabsVisible(initialScrollY <= 25);

    // Listen to scroll events - try multiple approaches
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    
    // Also listen on documentElement and body
    document.documentElement.addEventListener('scroll', handleScroll, { passive: true });
    document.body.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
      document.documentElement.removeEventListener('scroll', handleScroll);
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, [currentPage, selectedGame]);
  
  // Debug: Log profile changes
  useEffect(() => {
    if (user) {
      console.log('User:', user.id);
      console.log('UserProfile:', userProfile);
      console.log('Loading:', loading);
    }
  }, [user, userProfile, loading]);

  // Close auth modal when user logs in and profile is loaded
  useEffect(() => {
    if (user && !loading && showAuth) {
      // Wait a moment for profile to load, then close modal
      const timer = setTimeout(() => {
        setShowAuth(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user, loading, showAuth, userProfile]);

  // Fetch unread notifications count on mount and when user changes
  useEffect(() => {
    if (!user) {
      setUnreadNotificationsCount(0);
      return;
    }

    const fetchUnreadCount = async () => {
      try {
        // Get all user's bets
        const { data: allBets } = await supabase
          .from('bets')
          .select('game_id')
          .eq('user_id', user.id);

        if (!allBets || allBets.length === 0) {
          setUnreadNotificationsCount(0);
          return;
        }

        const gameIds = [...new Set(allBets.map(bet => bet.game_id))];

        // Get ended games
        const { data: endedGames } = await supabase
          .from('games')
          .select('id')
          .in('id', gameIds)
          .eq('status', 'ended')
          .not('winner_side', 'is', null);

        if (!endedGames) {
          setUnreadNotificationsCount(0);
          return;
        }

        // Count unread (not viewed)
        const unreadCount = endedGames.filter(game => {
          return localStorage.getItem(`notification_viewed_${game.id}`) !== 'true';
        }).length;

        setUnreadNotificationsCount(unreadCount);
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };

    fetchUnreadCount();
    
    // Refresh count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      // Prepare email data
      const emailData = {
        email: email.toLowerCase().trim()
      };

      // If user is logged in, also save their user_id
      if (user) {
        emailData.user_id = user.id;
      }

      // Save email to Supabase
      const { error: supabaseError } = await supabase
        .from('email_subscriptions')
        .insert([emailData]);

      if (supabaseError) {
        // Check if it's a duplicate email error
        if (supabaseError.code === '23505') {
          setError('This email is already registered');
        } else if (supabaseError.code === 'PGRST301' || supabaseError.message?.includes('permission denied') || supabaseError.message?.includes('401')) {
          setError('Permission denied. Please make sure the email_subscriptions table exists and RLS policies are set up correctly.');
          console.error('RLS/Permission error:', supabaseError);
        } else {
          setError('Something went wrong. Please try again.');
          console.error('Supabase error:', supabaseError);
        }
        setIsLoading(false);
        return;
      }
      
      console.log('Email saved successfully');

      // Success
      setSubmitted(true);
      setEmail('');
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Error saving email:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      console.log('Logging out...');
      // Reset page state first
      setCurrentPage('games');
      setEmail('');
      setSubmitted(false);
      setError('');
      
      // Then sign out
      const { error } = await signOut();
      if (error) {
        console.error('Logout error:', error);
        // Even if there's an error, try to clear local state
        setCurrentPage('games');
      } else {
        console.log('Logout successful');
      }
    } catch (err) {
      console.error('Logout error:', err);
      // Clear state even on error
      setCurrentPage('games');
    }
  };

  // Show main page by default, with optional login
  return (
    <div className="App">
      <div className="user-header">
        <nav className="main-nav">
          <button
            className="brand-title"
            onClick={() => {
              setCurrentPage('games');
              setSelectedGame(null);
              setSearchInputValue('');
              setSearchQuery('');
            }}
          >
            <img src="/images/logo.png" alt="Votemarket logo" className="brand-logo" />
            <span className="brand-text">Votemarket</span>
          </button>
          <div className="search-container">
            <form 
              className="search-input-wrapper"
              onSubmit={(e) => {
                e.preventDefault();
                setSearchQuery(searchInputValue);
              }}
            >
              <img src="/images/search.png" alt="Search" className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search votes..."
                value={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setSearchQuery(searchInputValue);
                  }
                }}
              />
              {searchInputValue && (
                <img 
                  src="/images/cross.png" 
                  alt="Clear" 
                  className="search-clear-icon"
                  onClick={(e) => {
                    e.preventDefault();
                    setSearchInputValue('');
                    setSearchQuery('');
                  }}
                />
              )}
            </form>
          </div>
        </nav>
        <div className="user-info">
          {user && (
            <div className="notifications-wrapper" style={{ position: 'relative' }}>
              <button
                className="notifications-bell-btn"
                onClick={() => setShowNotifications(!showNotifications)}
                title="View game results"
              >
                <img 
                  src="/bell.png" 
                  alt="Notifications" 
                  className="notifications-bell-icon"
                />
                {unreadNotificationsCount > 0 && (
                  <span className="notifications-bell-badge">{unreadNotificationsCount}</span>
                )}
              </button>
              <Notifications
                user={user}
                isOpen={showNotifications}
                onClose={() => setShowNotifications(false)}
                onUnreadCountChange={setUnreadNotificationsCount}
                onGameClick={(game) => {
                  setSelectedGame(game);
                  setCurrentPage('game-detail');
                  setShowNotifications(false);
                }}
              />
            </div>
          )}
          {user ? (
            <button
              className="user-email user-email-link"
              onClick={() => setCurrentPage('profile')}
              title={userProfile?.nickname || (loading ? 'Loading...' : user.email?.split('@')[0] || 'User')}
            >
              <img 
                src="/images/icon.png" 
                alt="User" 
                className="user-email-icon"
              />
            </button>
          ) : (
            <button className="login-header-btn" onClick={() => {
              setAuthMode('signup');
              setShowAuth(true);
            }}>
              Sign Up
            </button>
          )}
        </div>
      </div>

      {showAuth && !user && !loading ? (
        <div className="auth-overlay">
          <div className="auth-modal">
            <button className="close-auth-btn" onClick={() => setShowAuth(false)}>×</button>
            {authMode === 'login' ? (
              <Login onToggleMode={() => setAuthMode('signup')} onSuccess={() => setShowAuth(false)} />
            ) : (
              <Signup onToggleMode={() => setAuthMode('login')} onSuccess={() => setShowAuth(false)} />
            )}
          </div>
        </div>
      ) : null}

      {showCreateGame && (
        <div className="create-game-overlay" onClick={(e) => {
          if (e.target.className === 'create-game-overlay') {
            setShowCreateGame(false);
          }
        }}>
          <div className="create-game-modal">
            <button 
              className="close-auth-btn" 
              onClick={() => setShowCreateGame(false)}
              style={{ position: 'absolute', top: '-40px', right: '0' }}
            >
              ×
            </button>
            <CreateGame
              onGameCreated={() => {
                setShowCreateGame(false);
              }}
              onClose={() => setShowCreateGame(false)}
              onLoginRequired={() => {
                setAuthMode('signup');
                setShowAuth(true);
              }}
            />
          </div>
        </div>
      )}

      {/* Tabs fixed to main menu - 10px from it */}
      {currentPage === 'games' && !selectedGame && (
        <div 
          className={`games-tabs-wrapper ${tabsVisible ? '' : 'tabs-hidden'}`}
          style={{ top: `${tabsWrapperPaddingTop}px` }}
        >
          <div className="games-tabs-container">
            <div className="games-tabs">
              <span 
                className={`games-tab ${activeTab === 'explore' ? 'active' : ''}`}
                onClick={() => setActiveTab('explore')}
              >
                Explore
              </span>
              <span 
                className={`games-tab ${activeTab === 'watchlist' ? 'active' : ''}`}
                onClick={() => setActiveTab('watchlist')}
              >
                Watchlist
              </span>
            </div>
            <button
              className="create-vote-btn"
              onClick={() => {
                setShowCreateGame(true);
              }}
            >
              + Create Vote
            </button>
          </div>
        </div>
      )}

      {currentPage === 'games' ? (
        selectedGame ? (
          <GameDetail 
            gameId={selectedGame.id} 
            onBack={() => {
              setSelectedGame(null);
              setCurrentPage('games');
            }}
            onLoginRequired={() => setShowAuth(true)}
          />
        ) : (
          <GamesList 
            onSelectGame={(game) => {
              setSelectedGame(game);
              setCurrentPage('game-detail');
            }}
            showCreateGame={showCreateGame}
            setShowCreateGame={setShowCreateGame}
            searchQuery={searchQuery}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onLoginRequired={() => {
              setAuthMode('signup');
              setShowAuth(true);
            }}
          />
        )
      ) : currentPage === 'game-detail' && selectedGame ? (
        <GameDetail 
          gameId={selectedGame.id} 
          onBack={() => {
            setSelectedGame(null);
            setCurrentPage('games');
          }}
          onLoginRequired={() => setShowAuth(true)}
        />
      ) : currentPage === 'profile' && user ? (
        <Profile onLogout={() => setCurrentPage('games')} />
      ) : (
        <div className="coming-soon-container">
          <div style={{ color: '#94a3b8', fontSize: '18px', marginTop: '40px' }}>
            Please log in to view your wallet
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
